import React, { useEffect, useRef, useState } from 'react';
import { readSession, logout } from '../../auth/cognito';
import { signOutEverywhere } from '../../lib/signOutEverywhere';
import { useQueueStats } from '../../hooks/useQueueStats';
import { headerFigures, breakdownRows } from '../../lib/queueStats';
import Wordmark from '../Brand/Wordmark';
import '../../styles/header-v3.css';

// Console header (#113, epic #117, 2026-09-22): the frozen v3 spec's header,
// on every post-sign-in screen (idle, on a call, wrap-up, ringing, waiting
// for Connect). Three parts, left to right:
//
//   1. The ONE knktr wordmark. It moved here from the left rail (vault #187
//      put it there because the rail showed on every screen; the header is
//      that place now, and the rail is a working surface).
//   2. The stats shell: IN QUEUE / LONGEST WAIT / SLA / AI CONTAINED /
//      YOUR AHT / YOUR CSAT as PLACEHOLDERS ONLY. None of these six figures
//      exists in the console or the Lambdas (checked 2026-09-21, vault #216),
//      so each shows a dash. Wiring real data is a separate future ticket.
//   3. Identity + the sign-out menu. The session keeps only the signed-in
//      email (auth/cognito.js sessionFrom), so the avatar's initials come from
//      the email and the spec's name line is omitted rather than invented.
//      Sign-out is a non-negotiable security control (Storm, 2026-09-21): it is
//      the FIRST item of a click-to-open menu on the avatar, one click deep,
//      wired to the same logout() the rail's button used to call.
//      Backlog 192 (2026-10-21): it now signs out of Amazon Connect too
//      (lib/signOutEverywhere.js: refused during a contact, Offline, Connect
//      logout, then this logout()), so a shared computer never hands the next
//      person the previous agent's phone.
//
// Component-local state only (the menu's open/closed and the sign-out
// note), no new data.
const STATS = ['IN QUEUE', 'LONGEST WAIT', 'SLA', 'AI CONTAINED', 'YOUR AHT', 'YOUR CSAT'];
// Backlog 128 (Storm 2026-10-21, vault #367 reversed #184 for these two):
// IN QUEUE and LONGEST WAIT are live, for the agent's approved clients only
// (hooks/useQueueStats -> knktr-queue-stats). The other four stay dashes.
const LIVE_STATS = new Set(['IN QUEUE', 'LONGEST WAIT']);

// "storm.bolton@…" -> "SB", "storm@…" -> "S". Never the whole address.
export function initialsFrom(user) {
  const local = String(user || '').split('@')[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  return parts.slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';
}

export default function ConsoleHeader() {
  const user = readSession()?.user ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [signOutNote, setSignOutNote] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const { data: queueData } = useQueueStats();
  const live = headerFigures(queueData);
  const breakdown = breakdownRows(queueData);
  const liveValue = label => (label === 'IN QUEUE' ? live.inQueue : live.longestWait);
  const onSignOut = () => {
    setSignOutNote('Signing out…');
    signOutEverywhere({ cognitoLogout: logout, onBlocked: setSignOutNote });
  };
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const firstItemRef = useRef(null);

  // Close on a click outside or on Escape; focus the first item when opened.
  // On close, focus goes back to the trigger (menu-button pattern; review
  // 2026-09-22) unless the close came from a click that has already put the
  // focus somewhere else, in which case that wins.
  useEffect(() => {
    if (!menuOpen) return undefined;
    firstItemRef.current?.focus();
    const onDown = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      const active = document.activeElement;
      if (!active || active === document.body || wrapRef.current?.contains(active)) buttonRef.current?.focus();
    };
  }, [menuOpen]);

  return (
    <header className="console-header">
      <Wordmark className="hdr-brand-mark" />

      {/* IN QUEUE / LONGEST WAIT live (backlog 128); the rest stay placeholders. */}
      <div className="hdr-stats" aria-label="Queue and performance stats">
        {STATS.map((label, i) => (
          <React.Fragment key={label}>
            {i === 4 && <span className="hdr-stat-divider" aria-hidden="true" />}
            {LIVE_STATS.has(label) ? (
              <button
                type="button"
                className="hdr-stat hdr-stat-live"
                onClick={() => setBreakdownOpen(o => !o)}
                aria-expanded={breakdownOpen}
                aria-controls="hdr-queue-breakdown"
                title={label === 'LONGEST WAIT' && live.longestWaitClient ? `Longest wait: ${live.longestWaitClient}` : 'Your clients only. Click for each client.'}
              >
                <span className="hdr-stat-label">{label}</span>
                <span className={`hdr-stat-value${liveValue(label) ? ' is-live' : ''}`}>{liveValue(label) ?? '—'}</span>
              </button>
            ) : (
              <div className={`hdr-stat${i >= 4 ? ' hdr-stat-you' : ''}`}>
                <span className="hdr-stat-label">{label}</span>
                <span className="hdr-stat-value" title="Not available in this build">—</span>
              </div>
            )}
          </React.Fragment>
        ))}
        {breakdownOpen && (
          <div className="hdr-queue-breakdown" id="hdr-queue-breakdown" role="region" aria-label="Queues by client">
            {breakdown.rows.length ? breakdown.rows.map(r => (
              <div className="hdr-qb-row" key={r.key}>
                <span className="hdr-qb-name">{r.name}</span>
                <span className="hdr-qb-fig">{r.waiting} waiting</span>
                <span className="hdr-qb-fig">longest {r.longestWait}</span>
                <span className="hdr-qb-fig">{r.agents}</span>
              </div>
            )) : <div className="hdr-qb-row">No live queue figures yet.</div>}
            {breakdown.people && <div className="hdr-qb-people">People: {breakdown.people}</div>}
          </div>
        )}
      </div>

      <div className="hdr-identity" ref={wrapRef}>
        <button
          type="button"
          className="hdr-account"
          ref={buttonRef}
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls="hdr-account-menu"
          title="Console account"
        >
          <span className="hdr-avatar" aria-hidden="true">{initialsFrom(user)}</span>
          <span className="hdr-user">{user ?? 'Signed in'}</span>
        </button>
        {menuOpen && (
          <div className="hdr-menu" id="hdr-account-menu" role="menu" aria-label="Console account">
            {user && <div className="hdr-menu-user" role="presentation">{user}</div>}
            <button
              type="button"
              className="hdr-menu-item hdr-signout"
              role="menuitem"
              ref={firstItemRef}
              onClick={onSignOut}
              title="Sign out of the console and of Amazon Connect (sets you Offline first)"
            >
              Sign out
            </button>
            {signOutNote && <div className="hdr-menu-note" role="status">{signOutNote}</div>}
          </div>
        )}
      </div>
    </header>
  );
}
